import { Routes } from '../../../routes';

/**
 * Where a money-health row sends the operator.
 *
 * ## Why these are FILTER links and not detail links
 *
 * The panel has no parametric routes: `/orders/:id` and `/payouts/:id` do not
 * exist, and detail is a drawer held in page-local state. A `<Link to={
 * '/orders/' + id}>` would typecheck, render, pass every unit test and then
 * match only the `NotFound` catch-all when somebody clicks it during an
 * incident. Filed as AUT-331; until it lands, every link here is a list route
 * plus a query the list already understands.
 *
 * Both are built from `Routes.App.*` (rule #12) — the path is never a literal.
 */

/**
 * Min free-text length the orders list requires before it applies `q`
 * (mirrors the backend `MIN_SEARCH_LENGTH`, deliberately re-stated rather than
 * imported: production files in this package never cross-import between page
 * folders). Below it the filter is ignored and the click would land on the
 * whole orders list, which is a worse answer than no link.
 */
const MIN_ORDER_SEARCH_LENGTH = 3;

/**
 * A one-row orders list for this order number. `null` when there is nothing
 * searchable — `AdminMoneyHoldDto.orderNumber` is `null` when the order row
 * could not be read at all, and that row renders as plain text with no link.
 */
export const orderSearchLink = (orderNumber: string | null): string | null => {
  if (orderNumber === null) return null;
  const q = orderNumber.trim();
  if (q.length < MIN_ORDER_SEARCH_LENGTH) return null;
  return `${Routes.App.Orders}?${new URLSearchParams({ q }).toString()}`;
};

/**
 * The failed payouts of one inspector. `status=failed` is stated explicitly
 * rather than leaned on as the list's default — a default can move, and this
 * link promises a specific view.
 */
export const failedPayoutsLink = (inspectorId: string): string =>
  `${Routes.App.Payouts}?${new URLSearchParams({ status: 'failed', inspectorId }).toString()}`;
